import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import { usePets } from '../../../contexts/PetsContext';
import { useToast } from '../../../contexts/ToastContext';
import { formatGrams } from '../../../lib/format';

/**
 * Modal de confirmación para "dispenser vacío".
 *
 * Cierra el ciclo activo de aprendizaje y resetea dispenserStockGr a 0.
 * Es destructivo en el sentido de que el ciclo no se puede re-abrir, así
 * que pedimos confirmación explícita y mostramos las stats del ciclo
 * para que el usuario sepa qué está cerrando.
 */
const ConfirmEmptyModal = ({ group, dispenser, isOpen, onClose, onSuccess }) => {
  const { confirmEmpty } = usePets();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Stage 2: el modal opera sobre un dispenser específico.
  const targetDispenser = dispenser ?? group.dispensers?.[0] ?? null;
  const cycle = group.activeCycle;

  const handleConfirm = async () => {
    if (!targetDispenser) return;
    try {
      setSubmitting(true);
      setError(null);
      const result = await confirmEmpty(group.id, targetDispenser.id);
      if (result?.wasOutlier) {
        toast.info(
          `Ciclo cerrado. Se marcó como atípico y no afecta las estimaciones.`
        );
      } else {
        toast.success(`${targetDispenser.name} marcado como vacío.`);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo marcar como vacío.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !submitting && onClose()} title={`Marcar ${targetDispenser?.name ?? 'dispenser'} como vacío`}>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-amber-900">
            Resetea {targetDispenser?.name ?? 'el dispenser'} a 0 g y cierra el ciclo de aprendizaje activo del grupo.
            El próximo rellenado abre un ciclo nuevo.
          </p>
        </div>

        {cycle && (
          <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
            <p className="font-medium text-slate-900 mb-2">Resumen del ciclo activo</p>
            <div className="flex items-center justify-between text-slate-600">
              <span>Comidas servidas</span>
              <span className="font-medium text-slate-900">{cycle.feedingsCount ?? 0}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-slate-600">
              <span>Total rellenado</span>
              <span className="font-medium text-slate-900">
                {formatGrams(cycle.refillAmountGr ?? 0)}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-slate-600">
              <span>Total servido</span>
              <span className="font-medium text-slate-900">
                {formatGrams(cycle.totalAmountFedGr ?? 0)}
              </span>
            </div>
          </div>
        )}

        {!cycle && (
          <p className="text-sm text-slate-600">
            No hay un ciclo activo abierto. Solo se va a resetear el dispenser a 0 g.
          </p>
        )}

        {error && (
          <p className="text-sm text-status-red bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            fullWidth
            isLoading={submitting}
            onClick={handleConfirm}
          >
            Confirmar vacío
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmEmptyModal;
